import { useGlobalLayoutState } from '#contexts/GlobalLayoutContext';
import { EyeOutlined, ShareAltOutlined, SnippetsOutlined } from '@ant-design/icons';
import {
  EditOutlined,
  FileOutlined,
  LayoutSidebarRightOutlined,
  PrintOutlined,
} from '@vindr/icons';
import { OrderWorklist } from '@vindr/services/apis/order.api';
import { ORDER_STATUS } from '@vindr/services/constants/order.constant';
import { PERMISSIONS } from '@vindr/services/constants/permissions.constant';
import keycloak from '@vindr/services/keycloak';
import { ItemType } from '@vindr/ui/src/components/ui/PopupContextMenu/PopupContextMenu';
import { useTranslation } from 'react-i18next';

export const MENU_TYPE = {
  OPEN_VIEWER: 'open',
  OPEN_VIEWER_AND_REPORT: 'open_viewer_and_report',
  OPEN_SPLIT_WINDOW: 'open_split',
  OPEN_SPLIT_WINDOW_AND_REPORT: 'open_split_window_and_report',
  BOOKMARK: 'bookmark',
  SHARE: 'share',
  OPEN_REPORT: 'open_report',
  ORDER_MATCH: 'order_match',
  SHARE_STUDY: 'share_study',
  ATTACHMENT_FILE: 'attachment_file',
  UPDATE_ORDER: 'update_order',
  PRINT: 'print',
};

const useContextMenu = ({ handleContextMenuSelect }): ItemType<OrderWorklist>[] => {
  const { t } = useTranslation('Vindoc');
  const { iframe } = useGlobalLayoutState();
  const [iframeState] = iframe || [];

  const menuOpenViewer = iframeState?.open
    ? [
        {
          key: MENU_TYPE.OPEN_SPLIT_WINDOW,
          label: t('Open in Split Window'),
          icon: <LayoutSidebarRightOutlined className="text-3xl" />,
          onClick: (record) => handleContextMenuSelect(MENU_TYPE.OPEN_SPLIT_WINDOW, record),
          disabled: (record) =>
            !record?.study_instance_uid?.length ||
            !keycloak.hasOneOfPerm([PERMISSIONS.page.view_viewer]),
        },
        {
          key: MENU_TYPE.OPEN_SPLIT_WINDOW_AND_REPORT,
          label: t('Open in Split Window and Report'),
          icon: <LayoutSidebarRightOutlined className="text-3xl" />,
          onClick: (record) =>
            handleContextMenuSelect(MENU_TYPE.OPEN_SPLIT_WINDOW_AND_REPORT, record),
          disabled: (record) =>
            !record?.study_instance_uid?.length ||
            !keycloak.hasOneOfPerm([PERMISSIONS.page.view_viewer]),
        },
      ]
    : [
        {
          key: MENU_TYPE.OPEN_VIEWER,
          label: t('Open Viewer'),
          icon: <EyeOutlined className="text-3xl" />,
          onClick: (record) => handleContextMenuSelect(MENU_TYPE.OPEN_VIEWER, record),
          disabled: (record) => {
            return (
              !record?.study_instance_uid?.length ||
              !keycloak.hasOneOfPerm([PERMISSIONS.page.view_viewer])
            );
          },
        },
        {
          key: MENU_TYPE.OPEN_VIEWER_AND_REPORT,
          label: t('Open Viewer and Report'),
          icon: <EyeOutlined className="text-3xl" />,
          onClick: (record) => handleContextMenuSelect(MENU_TYPE.OPEN_VIEWER_AND_REPORT, record),
          disabled: (record) =>
            !record?.study_instance_uid?.length ||
            !keycloak.hasOneOfPerm([PERMISSIONS.page.view_viewer]),
        },
      ];

  return [
    ...menuOpenViewer,
    {
      key: MENU_TYPE.SHARE_STUDY,
      label: t('Share Study'),
      icon: <ShareAltOutlined className="text-3xl" />,
      onClick: (record) => handleContextMenuSelect(MENU_TYPE.SHARE_STUDY, record),
      disabled: (record) =>
        !record?.study_instance_uid?.length ||
        record?.status === ORDER_STATUS.CANCEL ||
        !keycloak.hasOneOfPerm([PERMISSIONS.worklist.share]),
    },
    {
      key: MENU_TYPE.OPEN_REPORT,
      label: t('Open Report'),
      icon: <SnippetsOutlined className="text-3xl" />,
      onClick: (record) => handleContextMenuSelect(MENU_TYPE.OPEN_REPORT, record),
      disabled: (record) =>
        record?.status === ORDER_STATUS.CANCEL ||
        !keycloak.hasOneOfPerm([PERMISSIONS.worklist.report]),
    },
    {
      key: MENU_TYPE.PRINT,
      label: t('Print'),
      icon: <PrintOutlined className="text-3xl" />,
      onClick: (record) => handleContextMenuSelect(MENU_TYPE.PRINT, record),
      disabled: (record) =>
        !(
          keycloak.hasOneOfPerm([PERMISSIONS.report.print]) &&
          record?.status === ORDER_STATUS.APPROVED
        ),
    },
    {
      key: MENU_TYPE.ATTACHMENT_FILE,
      label: t('File Attachment'),
      icon: <FileOutlined className="text-3xl" />,
      onClick: (record) => handleContextMenuSelect(MENU_TYPE.ATTACHMENT_FILE, record),
      disabled: (record) =>
        record?.status === ORDER_STATUS.CANCEL ||
        !keycloak.hasOneOfPerm([PERMISSIONS.worklist.attachment]),
    },
    {
      key: MENU_TYPE.UPDATE_ORDER,
      label: t('Edit Order'),
      icon: <EditOutlined className="text-3xl" />,
      onClick: (record) => handleContextMenuSelect(MENU_TYPE.UPDATE_ORDER, record),
      disabled: !keycloak.hasOneOfPerm([PERMISSIONS.order.update]),
    },
  ];
};

export default useContextMenu;
